import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Camera, Upload, X, Trash2, Image, ChevronLeft, ChevronRight,
  AlertCircle, Loader, Plus, Lock, Crown, Link2, Check, Globe2, Star, MoreHorizontal
} from 'lucide-react';
import jobPhotoService from '../../services/jobPhotoService';
import PhotoViewer from './PhotoViewer';
import ConfirmModal from '../ConfirmModal';
import PhotoCapture, { getSkippedBeforeIds, saveSkippedBeforeIds } from './PhotoCapture';
import PhotoActionSheet from './PhotoActionSheet';

/**
 * Photo type configuration
 */
const PHOTO_TYPES = {
  Before: { label: 'Before', icon: '📷', color: 'blue', description: 'Before starting work' },
  After: { label: 'After', icon: '✅', color: 'green', description: 'Completed work' },
  Issue: { label: 'Issue', icon: '⚠️', color: 'amber', description: 'Problems found', proOnly: true },
  Progress: { label: 'Progress', icon: '🔄', color: 'purple', description: 'Work in progress', proOnly: true },
  General: { label: 'General', icon: '📸', color: 'gray', description: 'Other photos', proOnly: true },
};

/**
 * Detect if user is on a mobile device
 */
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * JobPhotos component - Photo gallery with upload for jobs
 * Handles tier-aware limits and mobile camera capture
 */
const JobPhotos = ({ 
  jobId, 
  customerId,
  onPhotoCountChange,
  compact = false,
  readOnly = false,
}) => {
  const [photos, setPhotos] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedType, setSelectedType] = useState('Before');
  const [caption, setCaption] = useState('');
  
  // Pairing state for After photos
  const [selectedPairing, setSelectedPairing] = useState(null); // ID of Before photo to pair with
  const [showPairingStep, setShowPairingStep] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [approving, setApproving] = useState(null);   // photoId currently being toggled
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    photo: null,
    deleting: false
  });
  
  const fileInputRef = useRef(null);

  // Capture mode ('Before' | 'After' | null) and the device-local "no after needed" list
  const [captureMode, setCaptureMode] = useState(null);
  const [captureStartId, setCaptureStartId] = useState(null);
  const [skippedIds, setSkippedIds] = useState(() => getSkippedBeforeIds(jobId));
  const canCapture = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  const cameraInputRef = useRef(null);

  // Per-tile action sheet
  const [sheetPhotoId, setSheetPhotoId] = useState(null);
  const [sheetBusy, setSheetBusy] = useState(false);
  const sheetPhoto = sheetPhotoId ? photos.find(p => p.id === sheetPhotoId) : null;
  
  // Detect mobile device
  const isMobile = isMobileDevice();

  // Get unpaired Before photos for pairing selection
  const unpairedBeforePhotos = useMemo(() => {
    const pairedBeforeIds = new Set(
      photos
        .filter(p => p.type === 'After' && p.pairedWithPhotoId)
        .map(p => p.pairedWithPhotoId)
    );
    
    return photos.filter(p => 
      p.type === 'Before' && !pairedBeforeIds.has(p.id)
    );
  }, [photos]);

  // All Before photos (for pairing, even if already paired)
  const allBeforePhotos = useMemo(() => {
    return photos.filter(p => p.type === 'Before');
  }, [photos]);

  useEffect(() => {
    if (jobId) {
      loadPhotos();
      loadLimits();
    }
  }, [jobId]);

  useEffect(() => {
    setSkippedIds(getSkippedBeforeIds(jobId));
  }, [jobId]);

  // Befores still waiting for an After, in the order they were shot
  const needsAfterPhotos = useMemo(() => {
    const skipped = new Set(skippedIds);
    return unpairedBeforePhotos
      .filter(p => !skipped.has(p.id))
      .sort((a, b) => a.id - b.id);
  }, [unpairedBeforePhotos, skippedIds]);

  const openCapture = (mode, startId = null) => {
    setError(null);
    setCaptureStartId(startId);
    setCaptureMode(mode);
  };

  const handleCaptureAdded = (photo) => {
    setPhotos(prev => [...prev, photo]);
    onPhotoCountChange?.(photos.length + 1);
  };

  const handleSkippedChange = (ids) => {
    setSkippedIds(ids);
    saveSkippedBeforeIds(jobId, ids);
  };

  const loadPhotos = async () => {
    try {
      setLoading(true);
      const response = await jobPhotoService.getJobPhotos(jobId);
      setPhotos(response.data);
      onPhotoCountChange?.(response.data.length);
    } catch (err) {
      console.error('Error loading photos:', err);
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const loadLimits = async () => {
    try {
      const response = await jobPhotoService.getLimits();
      setLimits(response.data);
    } catch (err) {
      console.error('Error loading limits:', err);
    }
  };

  const canAddPhotos = () => {
    if (!limits) return true;
    if (limits.maxPhotosPerJob === -1) return true;
    return photos.length < limits.maxPhotosPerJob;
  };

  const isTypeAllowed = (type) => {
    if (!limits) return true;
    return limits.allowedTypes?.includes(type);
  };

  const getRemainingSlots = () => {
    if (!limits || limits.maxPhotosPerJob === -1) return null;
    return Math.max(0, limits.maxPhotosPerJob - photos.length);
  };

  const handleFileSelect = async (event, fromCamera = false) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (!canAddPhotos()) {
      setError(`Photo limit reached (${limits.maxPhotosPerJob} per job). Upgrade to add more.`);
      return;
    }

    const file = files[0];
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPEG, PNG, and WebP images are allowed');
      return;
    }

    const maxSizeBytes = (limits?.maxFileSizeMB || 10) * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError(`File too large. Maximum size is ${limits?.maxFileSizeMB || 10}MB`);
      return;
    }

    if (!isTypeAllowed(selectedType)) {
      setError(`Photo type "${selectedType}" requires Pro or higher`);
      return;
    }

    // If uploading an After photo and there are Before photos, show pairing step
    if (selectedType === 'After' && allBeforePhotos.length > 0) {
      setPendingFile(file);
      setShowPairingStep(true);
    } else {
      // Upload directly
      await uploadPhoto(file, null);
    }
    
    event.target.value = '';
  };

  const handlePairingConfirm = async () => {
    if (!pendingFile) return;
    await uploadPhoto(pendingFile, selectedPairing);
    setPendingFile(null);
    setShowPairingStep(false);
    setSelectedPairing(null);
  };

  const handleSkipPairing = async () => {
    if (!pendingFile) return;
    await uploadPhoto(pendingFile, null);
    setPendingFile(null);
    setShowPairingStep(false);
    setSelectedPairing(null);
  };

  const uploadPhoto = async (file, pairedWithPhotoId = null) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);

      let latitude = null;
      let longitude = null;
      
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 60000
            });
          });
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
        } catch (geoErr) {
          console.log('Geolocation not available:', geoErr);
        }
      }

      setUploadProgress(20);

      const response = await jobPhotoService.uploadPhoto(
        jobId, 
        selectedType, 
        file, 
        caption || null,
        latitude,
        longitude,
        pairedWithPhotoId
      );

      setUploadProgress(100);
      setPhotos(prev => [...prev, response.data]);
      onPhotoCountChange?.(photos.length + 1);
      setCaption('');
      setShowUploadModal(false);
      
    } catch (err) {
      console.error('Error uploading photo:', err);
      const errorMsg = err.response?.data?.message || 
                       err.response?.data?.error || 
                       'Failed to upload photo';
      setError(errorMsg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Show delete confirmation
  const confirmDelete = (photo, e) => {
    e?.stopPropagation(); // Prevent opening photo viewer
    setDeleteConfirm({
      isOpen: true,
      photo,
      deleting: false
    });
  };

  const handleToggleApproval = async (photo, e) => {
  e?.stopPropagation();
  if (approving === photo.id) return;
  setApproving(photo.id);
  try {
    const newValue = !photo.isWebsiteApproved;
    await jobPhotoService.toggleWebsiteApproval(photo.id, newValue);
    setPhotos(prev => prev.map(p => {
      // Update the toggled photo
      if (p.id === photo.id) return { ...p, isWebsiteApproved: newValue, isFeatured: newValue ? p.isFeatured : false };
      // If unapproving a Before, also clear its paired After
      if (!newValue && p.pairedWithPhotoId === photo.id) return { ...p, isWebsiteApproved: false, isFeatured: false };
      // If unapproving an After, also clear its paired Before
      if (!newValue && photo.pairedWithPhotoId === p.id) return { ...p, isWebsiteApproved: false, isFeatured: false };
      return p;
    }));
  } catch (err) {
    console.error('Error toggling website approval:', err);
    setError('Failed to update website approval');
  } finally {
    setApproving(null);
  }
};
 
const handleToggleFeatured = async (photo, e) => {
  e?.stopPropagation();
  if (approving === photo.id) return;
  if (!photo.isWebsiteApproved) return;
  setApproving(photo.id);
  try {
    const newValue = !photo.isFeatured;
    await jobPhotoService.toggleFeatured(photo.id, newValue);
    setPhotos(prev => prev.map(p => {
      if (p.id === photo.id) return { ...p, isFeatured: newValue };
      const isPartner = p.pairedWithPhotoId === photo.id || photo.pairedWithPhotoId === p.id;
      if (isPartner && p.isWebsiteApproved) return { ...p, isFeatured: newValue };
      return p;
    }));
  } catch (err) {
    console.error('Error toggling featured:', err);
    setError('Failed to update featured status');
  } finally {
    setApproving(null);
  }
};

  // Handle actual deletion
  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.photo) return;
    
    setDeleteConfirm(prev => ({ ...prev, deleting: true }));
    
    try {
      await jobPhotoService.deletePhoto(deleteConfirm.photo.id);
      removePhotoLocal(deleteConfirm.photo.id);
      onPhotoCountChange?.(photos.length - 1);
      setSelectedPhoto(null);
      setDeleteConfirm({ isOpen: false, photo: null, deleting: false });
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Failed to delete photo');
      setDeleteConfirm(prev => ({ ...prev, deleting: false }));
    }
  };

  // Remove a photo locally and release any After that was matched to it
  const removePhotoLocal = (photoId) => {
    setPhotos(prev =>
      prev
        .filter(p => p.id !== photoId)
        .map(p => (p.pairedWithPhotoId === photoId ? { ...p, pairedWithPhotoId: null } : p))
    );
  };

  // Quiet re-fetch (no spinner) so the grid always matches the server after pairing/type changes
  const refreshPhotos = async () => {
    try {
      const response = await jobPhotoService.getJobPhotos(jobId);
      setPhotos(response.data);
      onPhotoCountChange?.(response.data.length);
    } catch (err) {
      console.error('Error refreshing photos:', err);
    }
  };

  const openSheet = (photo) => {
    setError(null);
    setSheetPhotoId(photo.id);
  };

  const runSheetAction = async (fn, failMsg) => {
    setSheetBusy(true);
    setError(null);
    try {
      await fn();
      await refreshPhotos();
      return true;
    } catch (err) {
      setError(err.response?.data?.message || failMsg);
      return false;
    } finally {
      setSheetBusy(false);
    }
  };

  const openPhotoViewer = (photo, index) => {
    setSelectedPhoto({ ...photo, index });
  };

  const navigatePhoto = (direction) => {
    if (!selectedPhoto) return;
    const newIndex = selectedPhoto.index + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      setSelectedPhoto({ ...photos[newIndex], index: newIndex });
    }
  };

  // Reset modal state when closing
  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setShowPairingStep(false);
    setPendingFile(null);
    setSelectedPairing(null);
    setCaption('');
  };

  // Compact mode for job cards
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {photos.length > 0 ? (
          <>
            <div className="flex -space-x-2">
              {photos.slice(0, 3).map((photo, idx) => (
                <img
                  key={photo.id}
                  src={photo.url}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover border-2 border-white"
                  style={{ zIndex: 3 - idx }}
                />
              ))}
            </div>
            <span className="text-xs text-gray-500">
              {photos.length} photo{photos.length !== 1 ? 's' : ''}
            </span>
          </>
        ) : (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Camera size={12} />
            No photos
          </span>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera size={18} className="text-gray-500" />
          <h3 className="font-medium text-gray-900">Job Photos</h3>
          {photos.length > 0 && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {photos.length}
            </span>
          )}
        </div>
        
        {!readOnly && (
          <div className="flex items-center gap-2">
            {limits && limits.maxPhotosPerJob !== -1 && (
              <span className={`text-xs ${
                getRemainingSlots() === 0 ? 'text-red-600' : 'text-gray-500'
              }`}>
                {getRemainingSlots()} of {limits.maxPhotosPerJob} remaining
              </span>
            )}
            
            <button
              onClick={() => setShowUploadModal(true)}
              disabled={!canAddPhotos()}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                canAddPhotos() 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Plus size={16} />
              Add Photo
            </button>
          </div>
        )}
      </div>

      {/* Capture buttons */}
      {!readOnly && canCapture && canAddPhotos() && (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => openCapture('Before')}
            className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Camera size={18} />
            Shoot Befores
          </button>
          <button
            onClick={() => openCapture('After')}
            disabled={needsAfterPhotos.length === 0}
            className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
              needsAfterPhotos.length > 0
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Camera size={18} />
            Shoot Afters{needsAfterPhotos.length > 0 ? ` (${needsAfterPhotos.length})` : ''}
          </button>
        </div>
      )}

      {/* Befores still needing an After */}
      {!readOnly && needsAfterPhotos.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-900 mb-2">
            {needsAfterPhotos.length} Before{needsAfterPhotos.length !== 1 ? 's' : ''} still need{needsAfterPhotos.length === 1 ? 's' : ''} an After
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {needsAfterPhotos.map(p => (
              <button
                key={p.id}
                onClick={() => canCapture && openCapture('After', p.id)}
                className="flex-shrink-0 w-16 text-left"
              >
                <img
                  src={p.thumbnailUrl || p.url}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <span className="block text-[10px] text-amber-800 truncate mt-0.5">
                  {p.caption || 'No label'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{error}</p>
            {error.includes('limit') && limits?.upgradeTierForMorePhotos && (
              <a 
                href="/settings/subscription" 
                className="text-xs text-red-600 hover:underline mt-1 inline-block"
              >
                Upgrade to {limits.upgradeTierForMorePhotos} →
              </a>
            )}
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Photo limit warning */}
      {!canAddPhotos() && !error && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <Crown size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-amber-800 font-medium">Photo limit reached</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Your plan allows {limits?.maxPhotosPerJob} photos per job.
              {limits?.upgradeTierForMorePhotos && (
                <a 
                  href="/settings/subscription" 
                  className="text-amber-600 hover:underline ml-1"
                >
                  Upgrade to {limits.upgradeTierForMorePhotos} for more →
                </a>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Photos Grid */}
      {photos.length > 0 ? (
  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
    {photos.map((photo, index) => {
      const typeConfig = PHOTO_TYPES[photo.type] || PHOTO_TYPES.General;
      const isPaired = photo.type === 'After' && photo.pairedWithPhotoId;
      const isApproving = approving === photo.id;
 
      return (
        <div
          key={photo.id}
          className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer group"
          onClick={() => openPhotoViewer(photo, index)}
        >
          <img
            src={photo.thumbnailUrl || photo.url}
            alt={photo.caption || `Photo ${index + 1}`}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 20%' }}
            loading="lazy"
          />
 
          {/* Type badge */}
          <div className={`absolute top-1 left-1 px-1 py-0.5 rounded text-[10px] whitespace-nowrap font-medium flex items-center gap-0.5 ${
            typeConfig.color === 'blue'   ? 'bg-blue-100 text-blue-700' :
            typeConfig.color === 'green'  ? 'bg-green-100 text-green-700' :
            typeConfig.color === 'amber'  ? 'bg-amber-100 text-amber-700' :
            typeConfig.color === 'purple' ? 'bg-purple-100 text-purple-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {photo.type}
            {isPaired && <Link2 size={10} className="ml-0.5" />}
          </div>
 
                  {/* Website / featured status pills */}
          {photo.isWebsiteApproved && (
            <div className="absolute bottom-1 left-1 flex items-center gap-1">
              <span className="flex items-center gap-0.5 bg-green-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                <Globe2 size={10} /> Site
              </span>
              {photo.isFeatured && (
                <span className="flex items-center gap-0.5 bg-amber-400 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                  <Star size={10} className="fill-white" /> Featured
                </span>
              )}
            </div>
          )}

          {/* Always-visible actions button (works on touch, no hover needed) */}
          {!readOnly && (
            <button
              onClick={(e) => { e.stopPropagation(); openSheet(photo); }}
              className="absolute top-1 right-1 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/75 active:bg-black/80 transition-colors"
              aria-label="Photo actions"
            >
              {isApproving ? <Loader size={16} className="animate-spin" /> : <MoreHorizontal size={18} />}
            </button>
          )}
        </div>
      );
    })}
  </div>
) : (
  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
    <Image size={32} className="mx-auto text-gray-300 mb-2" />
    <p className="text-gray-500 text-sm">No photos yet</p>
    {!readOnly && canAddPhotos() && (
      <button
        onClick={() => setShowUploadModal(true)}
        className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
      >
        Add your first photo
      </button>
    )}
  </div>
)}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {showPairingStep ? 'Match with Before Photo' : 'Add Photo'}
              </h3>
              <button 
                onClick={handleCloseUploadModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            {/* Pairing Step - Show when uploading After photo */}
            {showPairingStep ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Which "Before" photo does this "After" match?
                </p>
                
                {/* Before photos grid for selection */}
                <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {allBeforePhotos.map((photo) => {
                    const isSelected = selectedPairing === photo.id;
                    const isAlreadyPaired = photos.some(
                      p => p.type === 'After' && p.pairedWithPhotoId === photo.id
                    );
                    
                    return (
                      <button
                        key={photo.id}
                        onClick={() => setSelectedPairing(isSelected ? null : photo.id)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          isSelected 
                            ? 'border-green-500 ring-2 ring-green-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={photo.thumbnailUrl || photo.url}
                          alt={photo.caption || 'Before'}
                          className="w-full h-full object-cover"
                          style={{ objectPosition: 'center 20%' }}
                        />
                        {isSelected && (
                          <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                            <Check size={24} className="text-green-600" />
                          </div>
                        )}
                        {isAlreadyPaired && !isSelected && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gray-900/70 text-white text-xs py-0.5 text-center">
                            Paired
                          </div>
                        )}
                        {photo.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                            {photo.caption}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Preview of pending upload */}
                {pendingFile && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <img
                      src={URL.createObjectURL(pendingFile)}
                      alt="Upload preview"
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {pendingFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(pendingFile.size / 1024 / 1024).toFixed(1)} MB • After photo
                      </p>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={handleSkipPairing}
                    disabled={uploading}
                    className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Skip (No Match)
                  </button>
                  <button
                    onClick={handlePairingConfirm}
                    disabled={uploading || !selectedPairing}
                    className={`flex-1 px-4 py-2.5 font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                      selectedPairing
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <Loader size={16} className="animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Link2 size={16} />
                        Link & Upload
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Photo Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Photo Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(PHOTO_TYPES).map(([type, config]) => {
                      const allowed = isTypeAllowed(type);
                      return (
                        <button
                          key={type}
                          onClick={() => allowed && setSelectedType(type)}
                          disabled={!allowed}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            selectedType === type
                              ? config.color === 'blue' ? 'border-blue-500 bg-blue-50' :
                                config.color === 'green' ? 'border-green-500 bg-green-50' :
                                config.color === 'amber' ? 'border-amber-500 bg-amber-50' :
                                config.color === 'purple' ? 'border-purple-500 bg-purple-50' :
                                'border-gray-500 bg-gray-50'
                              : allowed
                                ? 'border-gray-200 hover:border-gray-300'
                                : 'border-gray-100 bg-gray-50 opacity-60'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-lg">{config.icon}</span>
                            {!allowed && <Lock size={12} className="text-gray-400" />}
                          </div>
                          <p className={`font-medium text-sm mt-1 ${
                            selectedType === type 
                              ? config.color === 'blue' ? 'text-blue-700' :
                                config.color === 'green' ? 'text-green-700' :
                                config.color === 'amber' ? 'text-amber-700' :
                                config.color === 'purple' ? 'text-purple-700' :
                                'text-gray-700'
                              : 'text-gray-700'
                          }`}>
                            {config.label}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">{config.description}</p>
                          {!allowed && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <Crown size={10} /> Pro+
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Hint about pairing */}
                {selectedType === 'After' && allBeforePhotos.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                    <Link2 size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800">
                      After selecting your photo, you'll be able to match it with a "Before" photo for side-by-side comparison.
                    </p>
                  </div>
                )}

                {/* Caption */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Caption (optional)
                  </label>
                  <input
                    type="text"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="e.g., Front porch, Driveway, Deck..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Tip: Use captions like "Front", "Side", "Back" to identify areas
                  </p>
                </div>

                {/* Upload Progress */}
                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Uploading...</span>
                      <span className="text-gray-900 font-medium">{uploadProgress}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Upload Buttons - Device Aware */}
                {!uploading && (
                  <div className="space-y-3">
                    {isMobile ? (
                      /* Mobile: Show both camera and gallery options */
                      <>
                        <button
                          onClick={() => cameraInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Camera size={20} />
                          Take Photo
                        </button>
                        <input
                          ref={cameraInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          capture="environment"
                          onChange={(e) => handleFileSelect(e, true)}
                          className="hidden"
                        />

                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <Image size={20} />
                          Choose from Gallery
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </>
                    ) : (
                      /* Desktop: Single upload button */
                      <>
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Upload size={20} />
                          Upload Photo
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </>
                    )}

                    <p className="text-xs text-gray-500 text-center">
                      Max file size: {limits?.maxFileSizeMB || 10}MB • JPEG, PNG, WebP
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Per-photo action sheet */}
      {sheetPhoto && (
        <PhotoActionSheet
          photo={sheetPhoto}
          photos={photos}
          photoTypes={PHOTO_TYPES}
          allowedTypes={limits?.allowedTypes}
          busy={sheetBusy || approving === sheetPhoto.id}
          error={error}
          onClose={() => setSheetPhotoId(null)}
          onSave={(payload, failMsg) =>
            runSheetAction(() => jobPhotoService.updatePhoto(sheetPhoto.id, payload), failMsg)
          }
          onToggleApproval={() => handleToggleApproval(sheetPhoto)}
          onToggleFeatured={() => handleToggleFeatured(sheetPhoto)}
          onDelete={() => { const p = sheetPhoto; setSheetPhotoId(null); confirmDelete(p); }}
        />
      )}

      {/* In-app camera */}
      {captureMode && (
        <PhotoCapture
          jobId={jobId}
          mode={captureMode}
          photos={photos}
          startBeforeId={captureStartId}
          skippedIds={skippedIds}
          onSkippedChange={handleSkippedChange}
          onPhotoAdded={handleCaptureAdded}
          onClose={() => setCaptureMode(null)}
        />
      )}

      {/* Photo Viewer - Using enhanced PhotoViewer component */}
      {selectedPhoto && (
        <PhotoViewer
          photos={photos}
          initialIndex={selectedPhoto.index}
          isOpen={!!selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
          jobId={jobId}
          readOnly={readOnly}
          onDeletePhoto={async (photoId) => {
            // Delete the photo
            await jobPhotoService.deletePhoto(photoId);
            // Update local state
            removePhotoLocal(photoId);
            onPhotoCountChange?.(photos.length - 1);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, photo: null, deleting: false })}
        onConfirm={handleDeleteConfirm}
        title="Delete Photo"
        message={`Are you sure you want to delete this ${deleteConfirm.photo?.type || ''} photo? This cannot be undone.`}
        confirmText="Delete Photo"
        cancelText="Cancel"
        variant="danger"
        loading={deleteConfirm.deleting}
      />
    </div>
  );
};

export default JobPhotos;